import React from "react";
import { toast } from "sonner";
import { Undo2 } from "lucide-react";

/**
 * undoableAction — optimistic UI pattern with deferred commit.
 *
 * Usage:
 *   undoableAction({
 *     label: "Deleted 3 records",
 *     onCommit: async () => api.post("/delete", { ids }),
 *     onUndo: () => refreshUI(),     // visual revert
 *     delayMs: 5000,
 *   });
 *
 * Shows a toast with an Undo button. If user clicks Undo before delay,
 * onCommit is NOT called and onUndo runs. Otherwise onCommit runs.
 */
export const undoableAction = ({ label, description, onCommit, onUndo, delayMs = 5000 }) => {
    let undone = false;

    const timer = setTimeout(async () => {
        if (undone) return;
        try {
            await onCommit?.();
        } catch (e) {
            // If commit fails, revert UI too
            toast.error(e?.response?.data?.detail || e?.message || "Action failed");
            onUndo?.();
        }
    }, delayMs);

    toast(label, {
        description,
        duration: delayMs,
        icon: React.createElement(Undo2, { className: "h-4 w-4 text-[#2EB4AC]" }),
        action: {
            label: "Undo",
            onClick: () => {
                undone = true;
                clearTimeout(timer);
                onUndo?.();
                toast.success("Undone");
            },
        },
    });
};
